/**
 * AYU Fortschritt — Streaks & Ziele
 * Sanfte Gamification + Fortschritts-Tracking
 */
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Flame,
  Target,
  Plus,
  Loader2,
  Trophy,
  BookOpen,
  Brain,
  MessageCircle,
  Sun,
  Star,
  Pause,
  Play,
  Trash2,
  Check,
  TrendingUp,
  Zap,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Users,
  Award,
  ArrowRight,
} from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Link } from "wouter";
import { useI18n } from "@/contexts/I18nContext";

const STREAK_ICONS: Record<string, React.ReactNode> = {
  tagebuch: <BookOpen className="w-5 h-5" />,
  meditation: <Brain className="w-5 h-5" />,
  coach: <MessageCircle className="w-5 h-5" />,
  tagesroutine: <Sun className="w-5 h-5" />,
  gesamt: <Star className="w-5 h-5" />,
};

const STREAK_LABELS: Record<string, string> = {
  tagebuch: "Tagebuch",
  meditation: "Meditation",
  coach: "Coach",
  tagesroutine: "Tagesroutine",
  gesamt: "Gesamt",
};

const STREAK_COLORS: Record<string, string> = {
  tagebuch: "from-amber-400 to-orange-500",
  meditation: "from-violet-400 to-purple-500",
  coach: "from-emerald-400 to-green-500",
  tagesroutine: "from-sky-400 to-blue-500",
  gesamt: "from-rose-400 to-pink-500",
};

const GOAL_ICONS: Record<string, string> = {
  meditation: "🧘",
  tagebuch: "📖",
  ernaehrung: "🥗",
  bewegung: "🏃",
  routine: "☀️",
  eigenes: "🎯",
};

const GOAL_PRESETS = [
  { title: "30 Tage Meditation", category: "meditation" as const, targetDays: 30, icon: "🧘" },
  { title: "21 Tage Tagebuch", category: "tagebuch" as const, targetDays: 21, icon: "📖" },
  { title: "14 Tage Morgenroutine", category: "routine" as const, targetDays: 14, icon: "☀️" },
  { title: "7 Tage bewusste Ernährung", category: "ernaehrung" as const, targetDays: 7, icon: "🥗" },
];

function StreakCard({ streak }: { streak: { streakType: string; currentStreak: number; longestStreak: number; totalActiveDays: number; lastActivityDate: string | null } }) {
  const { t } = useI18n();
  const isActiveToday = streak.lastActivityDate === new Date().toISOString().slice(0, 10);
  const gradient = STREAK_COLORS[streak.streakType] ?? "from-gray-400 to-gray-500";

  return (
    <div className="relative overflow-hidden rounded-xl bg-white/80 backdrop-blur-sm border border-[#d4dcc4] p-4 hover:shadow-md transition-shadow">
      {/* Flame indicator */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-white`}>
            {STREAK_ICONS[streak.streakType]}
          </div>
          <span className="text-sm font-medium text-[#2d3a1e]">{STREAK_LABELS[streak.streakType]}</span>
        </div>
        {isActiveToday && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
            {t("fortschritt.heuteAktiv")} ✓
          </span>
        )}
      </div>

      {/* Streak number */}
      <div className="flex items-end gap-1 mb-2">
        <span className="text-3xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {streak.currentStreak}
        </span>
        <span className="text-sm text-[#8a9680] mb-1">Tage</span>
        {streak.currentStreak >= 3 && (
          <Flame className={`w-5 h-5 mb-1 ml-1 ${streak.currentStreak >= 7 ? "text-orange-500" : "text-amber-400"}`} />
        )}
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-[10px] text-[#8a9680]">
        <span>{t("fortschritt.rekord")} {streak.longestStreak} {t("fortschritt.tage")}</span>
        <span>•</span>
        <span>{t("fortschritt.gesamtLabel")} {streak.totalActiveDays} {t("fortschritt.tage")}</span>
      </div>
    </div>
  );
}

function GoalCard({
  goal,
  onTrackProgress,
  onPause,
  onResume,
  onDelete,
}: {
  goal: {
    id: number;
    title: string;
    description: string | null;
    category: string;
    targetDays: number;
    completedDays: number;
    lastProgressDate: string | null;
    startDate: string;
    status: string;
    icon: string;
  };
  onTrackProgress: () => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const progress = Math.min(100, Math.round((goal.completedDays / goal.targetDays) * 100));
  const isCompletedToday = goal.lastProgressDate === new Date().toISOString().slice(0, 10);
  const isComplete = goal.status === "completed";
  const isPaused = goal.status === "paused";

  return (
    <Card className={`bg-white/80 backdrop-blur-sm ${isComplete ? "border-green-300" : isPaused ? "border-amber-300 opacity-70" : "border-[#d4dcc4]"}`}>
      <CardContent className="py-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">{goal.icon}</span>
            <div>
              <h3 className="text-sm font-medium text-[#2d3a1e]">{goal.title}</h3>
              {goal.description && (
                <p className="text-[10px] text-[#8a9680] mt-0.5">{goal.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isComplete && (
              isPaused ? (
                <button onClick={onResume} className="p-1 rounded hover:bg-green-50" title="Fortsetzen">
                  <Play className="w-3.5 h-3.5 text-green-500" />
                </button>
              ) : (
                <button onClick={onPause} className="p-1 rounded hover:bg-amber-50" title="Pausieren">
                  <Pause className="w-3.5 h-3.5 text-amber-500" />
                </button>
              )
            )}
            <button onClick={onDelete} className="p-1 rounded hover:bg-red-50" title="Löschen">
              <Trash2 className="w-3.5 h-3.5 text-[#8a9680] hover:text-red-400" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-2">
          <div className="flex justify-between text-[10px] text-[#8a9680] mb-1">
            <span>{goal.completedDays} / {goal.targetDays} Tage</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-[#eef2e6] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isComplete ? "bg-green-500" : "bg-gradient-to-r from-[#6b7f4e] to-[#8ba668]"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Action */}
        {isComplete ? (
          <div className="flex items-center gap-2 text-green-600 text-xs">
            <Trophy className="w-4 h-4" />
            <span className="font-medium">{t("fortschritt.zielErreicht")} 🎉</span>
          </div>
        ) : !isPaused && (
          <Button
            size="sm"
            onClick={onTrackProgress}
            disabled={isCompletedToday}
            className={`w-full text-xs h-8 ${
              isCompletedToday
                ? "bg-[#eef2e6] text-[#8a9680] cursor-default"
                : "bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white"
            }`}
          >
            {isCompletedToday ? (
              <>
                <Check className="w-3 h-3 mr-1" />
                {t("fortschritt.heuteErledigt")} ✓
              </>
            ) : (
              <>
                <Check className="w-3 h-3 mr-1" />
                {t("fortschritt.heuteAbhaken")}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * Gamification Explainer — Verständliche Erklärung des AYU-Pfads
 * ═══════════════════════════════════════════════════════════════════ */
function GamificationExplainer() {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-2xl border border-[#C9A96E]/30 bg-gradient-to-br from-white via-[#faf8f3] to-[#f5f0e5] overflow-hidden shadow-sm">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#C9A96E]/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C9A96E]/20 to-[#8b6914]/20 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-[#8b6914]" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-[#2d3a1e]">
              {t("fortschritt.ayuPfadWie")}
            </h3>
            <p className="text-[11px] text-[#8a9680]">
              {t("fortschritt.ayuPfadErklärung")}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-[#8a9680]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#8a9680]" />
        )}
      </button>

      {/* Expandable content */}
      {expanded && (
        <div className="px-5 pb-5 space-y-5 border-t border-[#C9A96E]/15">
          {/* Prana-Punkte */}
          <div className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-[#8b6914]" />
              <h4 className="text-sm font-bold text-[#2d3a1e]">Prana-Punkte — Deine Energie-Währung</h4>
            </div>
            <p className="text-xs text-[#5a6b4a] leading-relaxed mb-3">
              Prana (Sanskrit: Lebensenergie) sind die Punkte, die du für jede Aktivität in AYU sammelst. 
              Je aktiver du bist, desto mehr Prana sammelst du — und desto höher steigst du auf.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { icon: <BookOpen className="w-4 h-4" />, label: "Tagebuch", points: "+10", color: "bg-amber-50 text-amber-700 border-amber-200" },
                { icon: <MessageCircle className="w-4 h-4" />, label: "Coach", points: "+15", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                { icon: <Check className="w-4 h-4" />, label: "Check-in", points: "+5", color: "bg-sky-50 text-sky-700 border-sky-200" },
                { icon: <Users className="w-4 h-4" />, label: "Community", points: "+5", color: "bg-violet-50 text-violet-700 border-violet-200" },
              ].map((item) => (
                <div key={item.label} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border ${item.color}`}>
                  {item.icon}
                  <span className="text-[10px] font-medium">{item.label}</span>
                  <span className="text-sm font-bold">{item.points}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Streaks */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-orange-500" />
              <h4 className="text-sm font-bold text-[#2d3a1e]">Streaks — Dranbleiben wird belohnt</h4>
            </div>
            <p className="text-xs text-[#5a6b4a] leading-relaxed mb-3">
              Ein Streak zählt die aufeinanderfolgenden Tage, an denen du eine Aktivität ausgeführt hast. 
              Schreibst du z.B. 5 Tage hintereinander Tagebuch, hast du einen 5-Tage-Streak. 
              Wenn dein Streak in Gefahr ist (du warst heute noch nicht aktiv), bekommst du um 20:00 Uhr eine Erinnerung.
            </p>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 border border-orange-200">
              <div className="flex items-center gap-1">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-xs font-bold text-orange-700">3 Tage</span>
              </div>
              <ArrowRight className="w-3 h-3 text-orange-400" />
              <div className="flex items-center gap-1">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-xs font-bold text-orange-700">7 Tage</span>
              </div>
              <ArrowRight className="w-3 h-3 text-orange-400" />
              <div className="flex items-center gap-1">
                <Flame className="w-6 h-6 text-red-500" />
                <span className="text-xs font-bold text-red-700">30+ Tage</span>
              </div>
              <span className="text-[10px] text-orange-600 ml-auto">Flamme wächst!</span>
            </div>
          </div>

          {/* Badges */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-[#8b6914]" />
              <h4 className="text-sm font-bold text-[#2d3a1e]">Badges — Meilensteine feiern</h4>
            </div>
            <p className="text-xs text-[#5a6b4a] leading-relaxed mb-3">
              Badges sind Auszeichnungen für besondere Meilensteine: Dein erster Tagebuch-Eintrag, 
              7 Tage Streak, 100 Prana-Punkte und vieles mehr. Sammle sie alle auf deinem AYU-Pfad!
            </p>
            <div className="flex gap-2 flex-wrap">
              {[
                { emoji: "🌱", label: "Erster Eintrag" },
                { emoji: "🔥", label: "7-Tage-Streak" },
                { emoji: "🧘", label: "Erste Meditation" },
                { emoji: "💬", label: "Community-Beitrag" },
                { emoji: "🏆", label: "100 Prana" },
              ].map((badge) => (
                <div key={badge.label} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#f5f0e5] border border-[#C9A96E]/20 text-[10px] text-[#5a6b4a]">
                  <span>{badge.emoji}</span>
                  <span>{badge.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Level */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-[#8b6914]" />
              <h4 className="text-sm font-bold text-[#2d3a1e]">7 Level — Dein AYU-Pfad</h4>
            </div>
            <p className="text-xs text-[#5a6b4a] leading-relaxed mb-3">
              Je mehr Prana-Punkte du sammelst, desto höher steigst du auf. 
              Jedes Level hat einen Sanskrit-Namen und repräsentiert eine Stufe deiner Gesundheitsreise.
            </p>
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
              {[
                { level: 1, name: "Saat", color: "#8fbc8f", points: "0" },
                { level: 2, name: "Spross", color: "#6b8e23", points: "100" },
                { level: 3, name: "Blüte", color: "#daa520", points: "500" },
                { level: 4, name: "Frucht", color: "#cd853f", points: "1.500" },
                { level: 5, name: "Baum", color: "#8b4513", points: "5.000" },
                { level: 6, name: "Wald", color: "#556b2f", points: "15.000" },
                { level: 7, name: "Licht", color: "#b8860b", points: "50.000" },
              ].map((lvl, idx) => (
                <div key={lvl.level} className="flex items-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                      style={{ backgroundColor: lvl.color }}
                    >
                      {lvl.level}
                    </div>
                    <span className="text-[9px] font-medium text-[#2d3a1e]">{lvl.name}</span>
                    <span className="text-[8px] text-[#8a9680]">{lvl.points}P</span>
                  </div>
                  {idx < 6 && <ArrowRight className="w-3 h-3 text-[#C9A96E]/40 mx-0.5 shrink-0" />}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="pt-2">
            <Link href="/app/gamification">
              <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white text-xs font-medium transition-colors">
                <Trophy className="w-4 h-4" />
                {t("fortschritt.allesBadgesLevel")}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppFortschritt() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [newGoalTitle, setNewGoalTitle] = useState("");
  const [newGoalDays, setNewGoalDays] = useState(30);
  const [newGoalCategory, setNewGoalCategory] = useState<"meditation" | "tagebuch" | "ernaehrung" | "bewegung" | "routine" | "eigenes">("eigenes");

  const { data: streaks, isLoading: streaksLoading } = trpc.streaks.getAll.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const { data: goals, isLoading: goalsLoading, refetch: refetchGoals } = trpc.goals.getAll.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const createGoalMutation = trpc.goals.create.useMutation({
    onSuccess: () => {
      refetchGoals();
      setShowNewGoal(false);
      setNewGoalTitle("");
      toast.success(t("fortschritt.neuesZielErstellt"));
    },
  });

  const trackProgressMutation = trpc.goals.trackProgress.useMutation({
    onSuccess: (result) => {
      refetchGoals();
      if (result?.status === "completed") {
        toast.success("🎉 " + t("fortschritt.herzlichen"));
      } else {
        toast.success(t("fortschritt.fortschrittGespeichert"));
      }
    },
  });

  const updateStatusMutation = trpc.goals.updateStatus.useMutation({
    onSuccess: () => refetchGoals(),
  });

  const deleteGoalMutation = trpc.goals.delete.useMutation({
    onSuccess: () => {
      refetchGoals();
      toast.success(t("fortschritt.zielGeloescht"));
    },
  });

  const handleCreateGoal = () => {
    if (!client?.clientId || !newGoalTitle.trim()) return;
    createGoalMutation.mutate({
      clientId: client.clientId,
      title: newGoalTitle.trim(),
      category: newGoalCategory,
      targetDays: newGoalDays,
      icon: GOAL_ICONS[newGoalCategory] ?? "🎯",
    });
  };

  const handlePreset = (preset: typeof GOAL_PRESETS[0]) => {
    if (!client?.clientId) return;
    createGoalMutation.mutate({
      clientId: client.clientId,
      title: preset.title,
      category: preset.category,
      targetDays: preset.targetDays,
      icon: preset.icon,
    });
  };

  const activeGoals = useMemo(() => goals?.filter((g) => g.status === "active") ?? [], [goals]);
  const completedGoals = useMemo(() => goals?.filter((g) => g.status === "completed") ?? [], [goals]);
  const pausedGoals = useMemo(() => goals?.filter((g) => g.status === "paused") ?? [], [goals]);

  const isLoading = streaksLoading || goalsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8]">
        <Loader2 className="w-8 h-8 animate-spin text-[#6b7f4e]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f8f8] p-4 lg:p-8">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("fortschritt.title")}
          </h1>
          <p className="text-[#5a6b4a] text-sm">
            {t("fortschritt.subtitle")}
          </p>
        </div>

        {/* ═══ GAMIFICATION ERKLÄRUNG ═══ */}
        <GamificationExplainer />

        {/* ═══ STREAKS ═══ */}
        <section>
          <h2 className="text-lg font-semibold text-[#2d3a1e] flex items-center gap-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Flame className="w-5 h-5 text-orange-500" />
            {t("fortschritt.deineSerien")}
          </h2>
          {(streaks?.length ?? 0) === 0 ? (
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="py-8 text-center">
                <Flame className="w-10 h-10 text-[#8a9680] mx-auto mb-2" />
                <p className="text-sm text-[#5a6b4a]">
                  {t("fortschritt.nochKeineSerien")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {streaks?.map((s) => (
                <StreakCard key={s.id} streak={s} />
              ))}
            </div>
          )}
        </section>

        {/* ═══ ZIELE ═══ */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#2d3a1e] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              <Target className="w-5 h-5 text-[#6b7f4e]" />
              {t("fortschritt.deineZiele")}
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNewGoal(!showNewGoal)}
              className="border-[#6b7f4e] text-[#6b7f4e] hover:bg-[#6b7f4e]/10 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              {t("fortschritt.neuesZiel")}
            </Button>
          </div>

          {/* Neues Ziel erstellen */}
          {showNewGoal && (
            <Card className="bg-white/80 backdrop-blur-sm mb-4 border-[#6b7f4e]/30">
              <CardContent className="py-4 space-y-3">
                <h3 className="text-sm font-medium text-[#2d3a1e]">{t("fortschritt.neuesZielErstellen")}</h3>

                {/* Vorlagen */}
                <div className="space-y-2">
                  <span className="text-xs text-[#8a9680]">{t("fortschritt.schnellstart")}</span>
                  <div className="flex flex-wrap gap-2">
                    {GOAL_PRESETS.map((preset) => (
                      <button
                        key={preset.title}
                        onClick={() => handlePreset(preset)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#eef2e6] text-[#5a6b4a] hover:bg-[#6b7f4e]/20 transition-colors"
                      >
                        <span>{preset.icon}</span>
                        {preset.title}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#d4dcc4] pt-3">
                  <span className="text-xs text-[#8a9680]">{t("fortschritt.eigenesZiel")}</span>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={newGoalTitle}
                      onChange={(e) => setNewGoalTitle(e.target.value)}
                      placeholder={t("fortschritt.zielTitel")}
                      className="flex-1 px-3 py-2 rounded-lg border border-[#d4dcc4] bg-[#f8f8f8] text-sm text-[#2d3a1e] placeholder:text-[#8a9680] focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30"
                    />
                    <input
                      type="number"
                      value={newGoalDays}
                      onChange={(e) => setNewGoalDays(Number(e.target.value))}
                      min={1}
                      max={365}
                      className="w-20 px-3 py-2 rounded-lg border border-[#d4dcc4] bg-[#f8f8f8] text-sm text-[#2d3a1e] text-center focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30"
                    />
                    <span className="text-xs text-[#8a9680] self-center">{t("fortschritt.tageLabel")}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleCreateGoal}
                    disabled={!newGoalTitle.trim()}
                    className="mt-2 bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white text-xs"
                  >
                    {t("fortschritt.zielErstellen")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Aktive Ziele */}
          {activeGoals.length > 0 && (
            <div className="space-y-3 mb-6">
              {activeGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onTrackProgress={() => trackProgressMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "" })}
                  onPause={() => updateStatusMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "", status: "paused" })}
                  onResume={() => updateStatusMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "", status: "active" })}
                  onDelete={() => deleteGoalMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "" })}
                />
              ))}
            </div>
          )}

          {/* Pausierte Ziele */}
          {pausedGoals.length > 0 && (
            <div className="space-y-3 mb-6">
              <h3 className="text-xs text-[#8a9680] font-medium uppercase tracking-wide">{t("fortschritt.pausiert")}</h3>
              {pausedGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onTrackProgress={() => {}}
                  onPause={() => {}}
                  onResume={() => updateStatusMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "", status: "active" })}
                  onDelete={() => deleteGoalMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "" })}
                />
              ))}
            </div>
          )}

          {/* Abgeschlossene Ziele */}
          {completedGoals.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs text-[#8a9680] font-medium uppercase tracking-wide flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5 text-amber-500" />
                {t("fortschritt.erreicht", { n: completedGoals.length } as any)}
              </h3>
              {completedGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onTrackProgress={() => {}}
                  onPause={() => {}}
                  onResume={() => {}}
                  onDelete={() => deleteGoalMutation.mutate({ goalId: goal.id, clientId: client?.clientId ?? "" })}
                />
              ))}
            </div>
          )}

          {/* Leerer Zustand */}
          {(goals?.length ?? 0) === 0 && !showNewGoal && (
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="py-8 text-center space-y-3">
                <Target className="w-10 h-10 text-[#8a9680] mx-auto" />
                <p className="text-sm text-[#5a6b4a]">
                  {t("fortschritt.keinZielBeschreibung")}
                </p>
                <Button
                  size="sm"
                  onClick={() => setShowNewGoal(true)}
                  className="bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white text-xs"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {t("fortschritt.ersteZielErstellen")}
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
}
